import type {
    Page,
    CreatePageRequest,
    AIPromptRequest,
    AIPromptResponse,
    ApiResponse,
} from '@hyvay/shared-types';
import apiClient from './api';

// Using mock data for now - will be replaced with actual API calls
const USE_MOCK = false;

class StudioService {
    async createPage(data: CreatePageRequest): Promise<ApiResponse<Page>> {
        if (USE_MOCK) {
            return { success: true, data: {} as Page };
        }

        try {
            const response = await apiClient.post<ApiResponse<Page>>('/studio/pages', data);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: 'CREATE_FAILED',
                message: error.message || 'Gagal membuat halaman baru',
            };
        }
    }

    async updatePage(
        id: string,
        data: Partial<Page>
    ): Promise<ApiResponse<Page>> {
        if (USE_MOCK) {
            return { success: true, data: { id } as Page };
        }

        try {
            const response = await apiClient.put<ApiResponse<Page>>(`/studio/pages/${id}`, data);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: 'UPDATE_FAILED',
                message: error.message || 'Gagal memperbarui halaman',
            };
        }
    }

    async deletePage(id: string): Promise<ApiResponse<void>> {
        if (USE_MOCK) {
            return { success: true, data: undefined };
        }

        try {
            const response = await apiClient.delete<ApiResponse<void>>(`/studio/pages/${id}`);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: 'DELETE_FAILED',
                message: error.message || 'Gagal menghapus halaman',
            };
        }
    }

    async processAIPrompt(
        data: AIPromptRequest
    ): Promise<ApiResponse<AIPromptResponse>> {
        if (USE_MOCK) {
            return { success: true, data: { success: true, message: '', generatedComponents: [] } };
        }

        try {
            const response = await apiClient.post<ApiResponse<AIPromptResponse>>(
                '/studio/ai-prompt',
                data
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: 'AI_FAILED',
                message: error.message || 'Gagal memproses prompt AI',
            };
        }
    }

    async saveComponents(
        pageId: string,
        components: any[]
    ): Promise<ApiResponse<void>> {
        if (USE_MOCK) {
            return { success: true, data: undefined };
        }

        try {
            const response = await apiClient.post<ApiResponse<void>>('/studio/components/save', { pageId, components });
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: 'SAVE_FAILED',
                message: error.message || 'Gagal menyimpan komponen',
            };
        }
    }

    async chat(
        applicationId: string,
        pageId: string,
        prompt: string
    ): Promise<ApiResponse<AIPromptResponse>> {
        if (USE_MOCK) {
            return { success: true, data: { success: true, message: '' } };
        }

        try {
            const response = await apiClient.post<ApiResponse<AIPromptResponse>>('/studio/chat', {
                applicationId,
                pageId,
                prompt,
            });
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: 'CHAT_FAILED',
                message: error.message || 'Failed to process chat',
            };
        }
    }

    /**
     * Streaming variant of chat(): connects to the SSE build endpoint and invokes
     * callbacks as build progress arrives (status, each action, final message, done).
     * Uses fetch + ReadableStream so the Bearer auth header can be sent.
     */
    async chatStream(
        applicationId: string,
        pageId: string,
        prompt: string,
        callbacks: {
            onStatus?: (status: string) => void;
            onAction?: (action: { id: string; type: string; label: string; status: string; details?: string }) => void;
            onMessage?: (message: string) => void;
            onDone?: (data: AIPromptResponse) => void;
            onError?: (message: string) => void;
        },
        signal?: AbortSignal,
    ): Promise<void> {
        const baseURL = apiClient.defaults.baseURL || '';
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`${baseURL}/studio/chat/build-stream`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ applicationId, pageId, prompt }),
            signal,
        });

        if (!res.ok || !res.body) {
            callbacks.onError?.(`Stream request failed (${res.status})`);
            return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const frames = buffer.split('\n\n');
            buffer = frames.pop() ?? '';
            for (const frame of frames) {
                const line = frame.split('\n').find((l) => l.startsWith('data:'));
                if (!line) continue;
                let event: any;
                try {
                    event = JSON.parse(line.slice(5).trim());
                } catch {
                    continue;
                }
                if (event.type === 'status') callbacks.onStatus?.(event.status);
                else if (event.type === 'action') callbacks.onAction?.(event.action);
                else if (event.type === 'message') callbacks.onMessage?.(event.message);
                else if (event.type === 'done') callbacks.onDone?.(event.data);
                else if (event.type === 'error') callbacks.onError?.(event.message);
            }
        }
    }

    async saveApplication(applicationId: string): Promise<ApiResponse<{ savedAt: string }>> {
        if (USE_MOCK) {
            return { success: true, data: { savedAt: '' } };
        }

        try {
            const response = await apiClient.post<ApiResponse<{ savedAt: string }>>('/studio/save', { applicationId });
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: 'SAVE_FAILED',
                message: error.message || 'Failed to save application',
            };
        }
    }

    async publishApplication(
        applicationId: string,
        subdomain: string,
        customDomain?: string
    ): Promise<ApiResponse<{ url: string; subdomain: string; publishedAt: string }>> {
        if (USE_MOCK) {
            return { success: true, data: { url: '', subdomain: '', publishedAt: '' } };
        }

        try {
            const response = await apiClient.post<ApiResponse<{ url: string; subdomain: string; publishedAt: string }>>('/studio/publish', {
                applicationId,
                subdomain,
                customDomain,
            });
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: 'PUBLISH_FAILED',
                message: error.message || 'Failed to publish application',
            };
        }
    }

    async checkSubdomainAvailability(
        subdomain: string,
        applicationId?: string,
    ): Promise<{ available: boolean; domain?: string }> {
        if (USE_MOCK) {
            return { available: true };
        }

        try {
            const response = await apiClient.get<ApiResponse<{ available: boolean; domain?: string }>>(`/studio/check-subdomain?subdomain=${subdomain}` +
                    (applicationId ? `&applicationId=${applicationId}` : ''));
            if (response.data.success) {
                return response.data.data;
            }
            return { available: false };
        } catch (error) {
            console.error('Check subdomain error:', error);
            return { available: false };
        }
    }

    async getCredits(): Promise<ApiResponse<{ credits: number }>> {
        if (USE_MOCK) {
            return { success: true, data: { credits: 10.0 } };
        }

        try {
            const response = await apiClient.get<ApiResponse<{ credits: number }>>('/studio/credits');
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: 'FETCH_FAILED',
                message: error.message || 'Failed to fetch credits',
            };
        }
    }

    /** Download the app source as a zip Blob (portable repo — runs locally). */
    async exportApplication(applicationId: string): Promise<Blob> {
        const response = await apiClient.get(`/studio/applications/${applicationId}/export`, {
            responseType: 'blob',
        });
        return response.data as Blob;
    }

    /** Backend (PocketBase) status: instance URL, admin access, collections. */
    async getBackendStatus(applicationId: string): Promise<ApiResponse<BackendStatus>> {
        try {
            const response = await apiClient.get<ApiResponse<BackendStatus>>(
                `/studio/applications/${applicationId}/backend`,
            );
            return response.data;
        } catch (error: any) {
            return { success: false, error: 'BACKEND_STATUS_FAILED', message: error.message };
        }
    }

    /** Re-apply backend/pb_schema.json to the app's dev instance. */
    async syncBackend(applicationId: string): Promise<ApiResponse<{ url?: string; hasSchema?: boolean }>> {
        try {
            const response = await apiClient.post<ApiResponse<{ url?: string; hasSchema?: boolean }>>(
                `/studio/applications/${applicationId}/backend/sync`,
            );
            return response.data;
        } catch (error: any) {
            return { success: false, error: 'BACKEND_SYNC_FAILED', message: error.message };
        }
    }
}

export interface BackendStatus {
    enabled: boolean;
    hasSchema: boolean;
    url?: string;
    adminUrl?: string;
    adminEmail?: string;
    adminPassword?: string;
    collections?: string[];
}

export const studioService = new StudioService();
export default studioService;

// ---- Media library (per-user assets referenced from prompts by assetId) -------

export interface MediaAsset {
    assetId: string;
    kind: 'image' | 'video';
    mimeType: string;
    bytes: number;
    width: number | null;
    height: number | null;
    originalName: string;
    alt: string | null;
    url: string;
    createdAt: string;
}

export interface MediaListResult {
    assets: MediaAsset[];
    usedBytes: number;
    quotaBytes: number;
    enabled: boolean;
}

export const mediaService = {
    async list(): Promise<ApiResponse<MediaListResult>> {
        try {
            const res = await apiClient.get<ApiResponse<MediaListResult>>('/studio/media');
            return res.data;
        } catch (e: any) {
            return { success: false, message: e?.response?.data?.message || e.message || 'Gagal memuat media' } as any;
        }
    },

    /** Multipart upload. `onProgress` gets 0..1 so the UI can show a real bar. */
    async upload(
        file: File,
        opts: { applicationId?: string; alt?: string; onProgress?: (fraction: number) => void } = {},
    ): Promise<ApiResponse<MediaAsset>> {
        try {
            const form = new FormData();
            form.append('file', file);
            if (opts.applicationId) form.append('applicationId', opts.applicationId);
            if (opts.alt) form.append('alt', opts.alt);
            const res = await apiClient.post<ApiResponse<MediaAsset>>('/studio/media', form, {
                // apiClient defaults to application/json; clearing it lets the browser
                // set multipart/form-data WITH the boundary, without which multer sees
                // no file. Also lift the 120s default — a 200MB video needs longer.
                headers: { 'Content-Type': undefined },
                timeout: 0,
                onUploadProgress: (e: any) => {
                    if (opts.onProgress && e.total) opts.onProgress(e.loaded / e.total);
                },
            });
            return res.data;
        } catch (e: any) {
            return { success: false, message: e?.response?.data?.message || e.message || 'Upload gagal' } as any;
        }
    },

    async remove(assetId: string): Promise<ApiResponse<{ assetId: string }>> {
        try {
            const res = await apiClient.delete<ApiResponse<{ assetId: string }>>(`/studio/media/${assetId}`);
            return res.data;
        } catch (e: any) {
            return { success: false, message: e?.response?.data?.message || e.message || 'Gagal menghapus' } as any;
        }
    },

    async setAlt(assetId: string, alt: string): Promise<ApiResponse<MediaAsset>> {
        try {
            const res = await apiClient.patch<ApiResponse<MediaAsset>>(`/studio/media/${assetId}`, { alt });
            return res.data;
        } catch (e: any) {
            return { success: false, message: e?.response?.data?.message || e.message || 'Gagal menyimpan' } as any;
        }
    },
};

export interface PublishedState {
    subdomain: string;
    customDomain: string | null;
    isActive: boolean;
    version: number;
    publishedAt: string | null;
    url: string;
}

/** Current publish state of an app, or null when it has never been published. */
export async function getPublishedState(applicationId: string): Promise<PublishedState | null> {
    try {
        const res = await apiClient.get<ApiResponse<PublishedState | null>>(
            `/studio/applications/${applicationId}/published`,
        );
        return res.data.success ? (res.data.data ?? null) : null;
    } catch {
        return null;
    }
}
